import { useCallback, useEffect, useState, useMemo } from 'react';
import { useFetch } from './useFetch';

/**
 * Fetch all resources of a given type, optionally limited.
 */
export function useAll(type, filter, options = {}) {
  const { limit = false } = options;
  const action = { verb: 'all', param: limit };
  return useFetch({ action, type, filter, options });
}

/**
 * Fetch the first resource that matches the parameters.
 */
export function useFirst(type, filter, options = {}) {
  const action = { verb: 'first' };
  return useFetch({ action, type, filter, options });
}

/**
 * Fetch a resource by ID.
 */
export function useFind(id, type, filter, options = {}) {
  const action = { verb: 'find', param: id };
  return useFetch({ action, type, filter, options });
}

/**
 * Paginated fetching of resources.
 * Keeps pagination state (page, per, total).
 */
export function usePaginate(paginate = {}, type, filter, options = {}) {
  const { page: initialPage = 1, per = 20 } = paginate;
  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(undefined);

  const action = { verb: 'paginate', param: { page, per } };
  const fetcher = useFetch({ action, type, filter, options });
  const { jsonapi } = fetcher.response;
  useEffect(() => {
    setTotal(jsonapi?.meta?.stats?.total?.count);
  }, [jsonapi]);

  const pagination = useMemo(
    () => ({
      page,
      setPage,
      per,
      total,
      isLastPage: page * per >= total,
    }),
    [page, setPage, per, total],
  );

  return useMemo(
    () => ({
      ...fetcher,
      pagination,
    }),
    [fetcher, pagination],
  );
}

/**
 * use for infinite scroll
 */
export function useScroll(scroll = {}, type, filter, options = {}) {
  // implémentation vraiment pas optimum...
  // c'est juste pour que ça fonctionne
  const { per: initialPer = 20 } = scroll;
  const [per, setPer] = useState(initialPer);
  const [total, setTotal] = useState(undefined);

  const action = { verb: 'paginate', param: { page: 1, per } };
  const fetcher = useFetch({ action, type, filter, options });
  const { jsonapi } = fetcher.response;
  useEffect(() => {
    setTotal(jsonapi?.meta?.stats?.total?.count);
  }, [jsonapi]);

  const nextPage = useCallback(() => {
    setPer((p) => p + initialPer);
  }, [initialPer]);

  const scrolling = useMemo(
    () => ({
      initialPer,
      per,
      total,
      nextPage,
      isLastPage: per >= total,
    }),
    [initialPer, nextPage, per, total],
  );

  return useMemo(
    () => ({
      ...fetcher,
      scrolling,
    }),
    [fetcher, scrolling],
  );
}
